import { Layout, Button, Space, Dropdown, Avatar, Drawer } from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  LoginOutlined,
  DownOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import "./Navbar.css";
import { useAuth } from "../../context/AuthContext";

const { Header } = Layout;

function Navbar() {
  const { user, setUser } = useAuth();
  const token = !!user;
  const isAdmin = user?.role === "admin";

  const navigate = useNavigate();
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 992;
      setIsMobile(mobile);
      if (!mobile) {
        setDrawerOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const backendBaseUrl = "http://localhost:5000";

  const getFullImageUrl = (imgPath) => {
    if (!imgPath || imgPath.trim() === "") return null;
    if (imgPath.startsWith("http")) return imgPath;
    return `${backendBaseUrl}${imgPath}`;
  };

  const profileImage = getFullImageUrl(user?.profileImg);
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setDrawerOpen(false);
    navigate("/");
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const profileMenu = !token
    ? [
      {
        key: "login",
        label: <Link to="/login">Login</Link>,
        icon: <LoginOutlined />,
      },
      {
        key: "register",
        label: <Link to="/register">Register</Link>,
        icon: <UserOutlined />,
      },
    ]
    : [
      {
        key: "personal-details",
        label: <Link to="/profile">Personal Details</Link>,
        icon: <UserOutlined />,
      },
      {
        key: "logout",
        label: "Logout",
        icon: <LogoutOutlined />,
        danger: true,
        onClick: handleLogout,
      },
    ];

  const isListingsActive =
    location.pathname === "/my-commercial-spaces" ||
    location.pathname === "/my-stalls" ||
    location.pathname === "/my-franchises" ||
    location.pathname === "/my-businesses" ||
    location.pathname === "/my-ads";

  const listingsMenu = {
    items: [
      {
        key: "my-commercial-spaces",
        label: <Link to="/my-commercial-spaces">My Commercial Spaces</Link>,
      },
      {
        key: "my-stalls",
        label: <Link to="/my-stalls">My Stalls</Link>,
      },
      {
        key: "my-franchises",
        label: <Link to="/my-franchises">My Franchises</Link>,
      },
      {
        key: "my-businesses",
        label: <Link to="/my-businesses">My Businesses</Link>,
      },
      {
        key: "my-ads",
        label: <Link to="/my-ads">My Ads</Link>,
      },
    ],
  };

  const initial = user?.name?.charAt(0)?.toUpperCase() || "U";

  return (
    <>
      <Header className="navbar">
        <div className="navbar__inner">
          <div className="navbar__brand">
            <Link to="/" className="navbar__logo">
              <img
                src="/images/logo.png"
                alt="Draptor"
                className="navbar__logo-img"
              />
              <span className="navbar__logo-text">DRAPTOR</span>
            </Link>
          </div>

          {!isMobile ? (
            <Space size="middle" className="navbar__actions">
              <Link
                to="/"
                className={`navbar__link ${location.pathname === "/" ? "navbar__link--active" : ""
                  }`}
              >
                Home
              </Link>

              <Link
                to="/explore"
                className={`navbar__link ${location.pathname === "/explore" ? "navbar__link--active" : ""
                  }`}
              >
                Explore
              </Link>

              {isAdmin && (
                <Link
                  to="/admin"
                  className={`navbar__link ${location.pathname.startsWith("/admin")
                    ? "navbar__link--active"
                    : ""
                    }`}
                >
                  Admin
                </Link>
              )}

              {token && (
                <Dropdown
                  menu={listingsMenu}
                  placement="bottom"
                  trigger={["hover"]}
                  classNames={{ root: "navbar__dropdown" }}
                >
                  <span
                    className={`navbar__link navbar__link--dropdown ${isListingsActive ? "navbar__link--active" : ""
                      }`}
                  >
                    My Listings <DownOutlined className="navbar__caret" />
                  </span>
                </Dropdown>
              )}

              <div className="navbar__cta-wrapper">
                <Link to={token ? "/add" : "/login"}>
                  <Button type="primary" className="navbar__primary-btn">
                    Add Yours
                  </Button>
                </Link>

                <span className="navbar__cta-badge-glow">Free</span>
              </div>

              <Dropdown
                menu={{ items: profileMenu }}
                placement="bottomRight"
                trigger={["hover"]}
                classNames={{ root: "navbar__dropdown navbar__dropdown--profile" }}
              >
                <div className="navbar__profile-btn">
                  <Space size={8}>
                    {user ? (
                      <Avatar
                        size="medium"
                        src={!imgError && profileImage ? profileImage : undefined}
                        onError={() => {
                          setImgError(true);
                          return false;
                        }}
                        style={!profileImage || imgError ? { backgroundColor: "#7265e6", color: "#fff" } : {}}
                      >
                        {(!profileImage || imgError) &&
                          user?.name?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    ) : (
                      <Avatar size="medium" icon={<UserOutlined />} />
                    )}

                    <span className="navbar__username">
                      {token ? user?.name?.split(" ")[0] : "Profile"}
                    </span>

                    <DownOutlined className="navbar__caret" />
                  </Space>
                </div>
              </Dropdown>
            </Space>
          ) : (
            <div className="navbar__mobile-actions">

              <div className="navbar__cta-wrapper">
                <Link to={token ? "/add" : "/login"}>
                  <Button type="primary" className="navbar__primary-btn navbar__primary-btn--mobile">
                    Add Yours
                  </Button>
                </Link>

                <span className="navbar__cta-badge-glow">Free</span>
              </div>

              <Button
                className="navbar__menu-toggle"
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerOpen(true)}
              />
            </div>
          )}
        </div>
      </Header>

      <Drawer
        title={
          <div className="navbar__drawer-brand">
            <img
              src="/images/logo.png"
              alt="Draptor"
              className="navbar__drawer-logo"
            />
            <span>DRAPTOR</span>
          </div>
        }
        placement="right"
        onClose={handleDrawerClose}
        open={drawerOpen}
        className="navbar__drawer"
        closeIcon={true}
        styles={{
          header: {
            display: "flex",
            flexDirection: "row-reverse",
            alignItems: "center",
          },
        }}
      >
        <div className="navbar__drawer-content">
          <Link
            to="/"
            onClick={handleDrawerClose}
            className={`navbar__drawer-link ${location.pathname === "/" ? "navbar__drawer-link--active" : ""
              }`}
          >
            Home
          </Link>

          <Link
            to="/explore"
            onClick={handleDrawerClose}
            className={`navbar__drawer-link ${location.pathname === "/explore"
              ? "navbar__drawer-link--active"
              : ""
              }`}
          >
            Explore
          </Link>

          {isAdmin && (
            <Link
              to="/admin"
              onClick={handleDrawerClose}
              className={`navbar__drawer-link ${location.pathname.startsWith("/admin")
                ? "navbar__drawer-link--active"
                : ""
                }`}
            >
              Admin
            </Link>
          )}

          {token && (
            <div className="navbar__drawer-section">
              <div className="navbar__drawer-section-title">My Listings</div>

              <div className="navbar__drawer-section-items">
                <Link to="/my-commercial-spaces" onClick={handleDrawerClose} className="navbar__drawer-link">
                  My Commercial Spaces
                </Link>

                <Link to="/my-stalls" onClick={handleDrawerClose} className="navbar__drawer-link">
                  My Stalls
                </Link>

                <Link to="/my-franchises" onClick={handleDrawerClose} className="navbar__drawer-link">
                  My Franchises
                </Link>

                <Link to="/my-businesses" onClick={handleDrawerClose} className="navbar__drawer-link">
                  My Businesses
                </Link>

                <Link to="/my-ads" onClick={handleDrawerClose} className="navbar__drawer-link">
                  My Ads
                </Link>
              </div>
            </div>
          )}

          <div className="navbar__drawer-section">
            <div className="navbar__drawer-user">
              {user ? (
                <Avatar
                  size="large"
                  src={!imgError && profileImage ? profileImage : undefined}
                  onError={() => {
                    setImgError(true);
                    return false;
                  }}
                  style={!profileImage || imgError ? { backgroundColor: "#7265e6", color: "#fff" } : {}}
                >
                  {(!profileImage || imgError) && initial}
                </Avatar>
              ) : (
                <Avatar size="large" icon={<UserOutlined />} />
              )}

              <div className="navbar__drawer-user-info">
                <span className="navbar__drawer-user-name">
                  {token ? user?.name || "User" : "Guest"}
                </span>
                <span className="navbar__drawer-user-subtext">
                  {token ? "Account options" : "Login or register"}
                </span>
              </div>
            </div>

            <div className="navbar__drawer-section-items">
              {!token ? (
                <>
                  <Link to="/login" onClick={handleDrawerClose} className="navbar__drawer-link">
                    Login
                  </Link>
                  <Link to="/register" onClick={handleDrawerClose} className="navbar__drawer-link">
                    Register
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/profile" onClick={handleDrawerClose} className="navbar__drawer-link">
                    Personal Details
                  </Link>

                  <button
                    type="button"
                    onClick={handleLogout}
                    className="navbar__drawer-link navbar__drawer-link--danger"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </Drawer>

      <div className="navbar__spacer" />
    </>
  );
}

export default Navbar;